import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import type { Tables } from "@/integrations/supabase/types";

type SubscriptionPlan = Tables<"subscription_plans">;

interface Business {
  id: string;
  name: string;
  plan_id: string | null;
  subscription_status: string;
}

interface SuperAdminSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  business: Business | null;
  onSuccess: () => void;
  mode: "upgrade" | "renew" | "suspend" | "activate";
}

export function SuperAdminSubscriptionDialog({
  open,
  onOpenChange,
  business,
  onSuccess,
  mode,
}: SuperAdminSubscriptionDialogProps) {
  const { toast } = useToast();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string>("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (open && business) {
      fetchPlans();
      // Always set to current plan initially
      setSelectedPlanId(business.plan_id || "");
    } else {
      // Reset when dialog closes
      setSelectedPlanId("");
    }
  }, [open, business]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("subscription_plans")
        .select("*")
        .order("price_ghs", { ascending: true });
      if (error) throw error;
      setPlans((data || []) as SubscriptionPlan[]);
    } catch (e: any) {
      toast({
        title: "Failed to load plans",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!business) return;

    setProcessing(true);
    try {
      if (mode === "upgrade" || mode === "renew") {
        if (!selectedPlanId) {
          toast({
            title: "Please select a plan",
            variant: "destructive",
          });
          setProcessing(false);
          return;
        }

        const now = new Date();
        const nextBilling = new Date(now);
        nextBilling.setMonth(nextBilling.getMonth() + 1);

        const updateData: any = {
          plan_id: selectedPlanId,
          subscription_status: "active",
          subscription_last_paid_at: now.toISOString(),
          subscription_next_payment_at: nextBilling.toISOString(),
        };

        const { data, error } = await supabase
          .from("businesses")
          .update(updateData)
          .eq("id", business.id)
          .select()
          .single();

        if (error) {
          console.error("Update error:", error);
          throw error;
        }

        // Verify the update
        if (!data || data.plan_id !== selectedPlanId) {
          throw new Error("Failed to update subscription plan. Please try again.");
        }

        toast({
          title: mode === "upgrade" ? "Subscription updated" : "Subscription renewed",
          description: `${business.name} is now on the ${plans.find(p => p.id === selectedPlanId)?.name || selectedPlanId} plan.`,
        });
      } else if (mode === "suspend") {
        const { error } = await supabase
          .from("businesses")
          .update({ subscription_status: "suspended" })
          .eq("id", business.id);

        if (error) throw error;

        toast({
          title: "Business suspended",
          description: `${business.name} has been suspended.`,
        });
      } else if (mode === "activate") {
        const { error } = await supabase
          .from("businesses")
          .update({ subscription_status: "active" })
          .eq("id", business.id);

        if (error) throw error;

        toast({
          title: "Business activated",
          description: `${business.name} is now active.`,
        });
      }

      onSuccess();
      onOpenChange(false);
    } catch (e: any) {
      toast({
        title: "Operation failed",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  if (!business) return null;

  const currentPlan = plans.find((p) => p.id === business.plan_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === "upgrade" && "Upgrade Subscription"}
            {mode === "renew" && "Renew Subscription"}
            {mode === "suspend" && "Suspend Business"}
            {mode === "activate" && "Activate Business"}
          </DialogTitle>
          <DialogDescription>
            {mode === "upgrade" && `Change subscription plan for ${business.name}`}
            {mode === "renew" && `Renew subscription for ${business.name}`}
            {mode === "suspend" && `Suspend ${business.name}? This will prevent them from accessing the platform.`}
            {mode === "activate" && `Activate ${business.name}? This will restore their access to the platform.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {mode === "upgrade" && (
            <>
              <div>
                <Label>Current Plan</Label>
                <div className="mt-1">
                  <Badge variant="outline">
                    {currentPlan?.name || business.plan_id || "Unknown"}
                  </Badge>
                  {currentPlan?.price_ghs && (
                    <span className="ml-2 text-sm text-muted-foreground">
                      ₵{currentPlan.price_ghs}/month
                    </span>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="plan-select">Select New Plan</Label>
                {loading ? (
                  <div className="mt-2 flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm text-muted-foreground">Loading plans...</span>
                  </div>
                ) : (
                  <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
                    <SelectTrigger id="plan-select" className="mt-2">
                      <SelectValue placeholder="Select a plan" />
                    </SelectTrigger>
                    <SelectContent>
                      {plans.map((plan) => (
                        <SelectItem key={plan.id} value={plan.id}>
                          <div className="flex items-center justify-between w-full">
                            <span>{plan.name}</span>
                            {plan.price_ghs && (
                              <span className="ml-4 text-sm text-muted-foreground">
                                ₵{plan.price_ghs}/month
                              </span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </>
          )}

          {mode === "renew" && (
            <div>
              <Label>Current Plan</Label>
              <div className="mt-1">
                <Badge variant="outline">
                  {currentPlan?.name || business.plan_id || "Unknown"}
                </Badge>
                {currentPlan?.price_ghs && (
                  <span className="ml-2 text-sm text-muted-foreground">
                    ₵{currentPlan.price_ghs}/month
                  </span>
                )}
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                This will set the subscription status to active. The business will continue on their current plan.
              </p>
            </div>
          )}

          {(mode === "suspend" || mode === "activate") && (
            <div>
              <Label>Current Status</Label>
              <div className="mt-1">
                <Badge variant={business.subscription_status === "active" ? "default" : "secondary"}>
                  {business.subscription_status}
                </Badge>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={processing}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={processing || (mode === "upgrade" && !selectedPlanId)}>
            {processing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {mode === "upgrade" && "Upgrade"}
            {mode === "renew" && "Renew"}
            {mode === "suspend" && "Suspend"}
            {mode === "activate" && "Activate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
